import { useEffect, useState } from 'react'
import { addDays, endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { useMediaQuery, Autocomplete as MuiAutocomplete, TextField, FormControl } from '@mui/material'
import { getCompetencePeriodFromSource } from './helpers/getCompetencePeriodFromSource'
import { parseCompetenceDate } from '@utils/dates/parseCompetenceDate'

import { RowBox } from '@components/shared/RowBox'
import { Input } from '@components/shared/Input'
import { EditIcon } from '@assets/svgs/EditIcon'
import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { Select } from '@components/shared/Select'
import { emotionTheme } from '@styles/theme'
import { Textarea } from '@components/shared/Textarea'
import { initialFormValues } from './form/initialFormValues'
import { createAddEditOtherServicesModalSchema } from './form/validations'
import { DatePicker } from '@components/shared/DatePicker'
import { setInitialValues } from './helpers/setInitialValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { useClientModal } from '@pages/InvoicingClientModal/context'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { useInvoicing } from '@pages/Invoicing/context'
import { useUser } from '@hooks/useUser'

import type { AddEditOtherServicesModalProps } from './types'
import type { AddEditOtherServicesModalFormProps } from './form/types'

import * as S from './styles'

export const AddEditOtherServicesModal = ({
  data,
  open,
  clientData,
  clientsList,
  competenceMinDate: competenceMinDateProp,
  competenceMaxDate: competenceMaxDateProp,
  onClose,
  onSubmitForm,
  canEdit = true,
  ...rest
}: AddEditOtherServicesModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const { isCreateNewOtherServiceLoading } = useClientModal()
  const { isCreateNewOtherServiceLoading: isCreateNewOtherServiceLoadingInvoicing } = useInvoicing()

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState<typeof clientData>(null)
  const isEdit = !!data

  const periodSource = clientData ?? selectedClient
  const periodFromSource = getCompetencePeriodFromSource(periodSource as Record<string, unknown> | null, filiData)

  let minDate = competenceMinDateProp ?? periodFromSource.minDate
  let maxDate = competenceMaxDateProp ?? periodFromSource.maxDate

  const filiMin = parseCompetenceDate(filiData?.startPeriod) ?? undefined

  // Restringe o calendário: 2º dia do mês anterior até o último dia do mês da competência
  if (filiMin) {
    minDate = addDays(startOfMonth(subMonths(filiMin, 1)), 1)
    maxDate = endOfMonth(filiMin)
  }

  const getCompetencePeriod = () => ({ minDate, maxDate })

  const methods = useForm<AddEditOtherServicesModalFormProps>({
    defaultValues: initialFormValues,
    resolver: createAddEditOtherServicesModalSchema(
      clientsList?.length && !clientData ? getCompetencePeriod : minDate,
      maxDate
    ) as unknown as Resolver<AddEditOtherServicesModalFormProps>
  })

  const { handleSubmit, reset } = methods

  const onError = (errors: FieldErrors<AddEditOtherServicesModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: AddEditOtherServicesModalFormProps) => {
    if (!canEdit) return

    onSubmitForm(formData)
  }

  const handleTotalNetValue = () => {
    if (methods.watch('grossTotalValue')) {
      const grossTotalValue = unMaskCurrency(methods.watch('grossTotalValue'))
      const tax = Number(methods.watch('tax') || 0)

      if (grossTotalValue) {
        const taxPercent = Number(tax) / 100
        const netTotalValue = grossTotalValue * (1 - taxPercent)

        methods.setValue('netTotalValue', netTotalValue.toFixed(2))
      }
    }
  }

  const handleTotalGrossValue = () => {
    if (methods.watch('grossUnitValue') && methods.watch('totalQuantity')) {
      const grossTotalValue = unMaskCurrency(methods.watch('grossUnitValue'))
      const totalQuantity = Number(methods.watch('totalQuantity'))

      methods.setValue('grossTotalValue', (grossTotalValue * totalQuantity).toFixed(2))
      methods.setValue('netTotalValue', (grossTotalValue * totalQuantity).toFixed(2))
    }
  }

  useEffect(() => {
    if (data || clientData) {
      reset(setInitialValues(data!, clientData!))
      if (!clientData && data?.clientId && clientsList?.length) {
        const client = clientsList.find((c) => c.cdClienteNl === data!.clientId)
        setSelectedClient(client ?? null)
      }
    }
  }, [data, clientData, open, clientsList])

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      setSelectedClient(null)
    }
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer={isMobile ? 'calc(100% - 20px)' : 'auto '}
        title={isEdit ? 'Editar outros serviços' : 'Adicionar um outro serviço'}
        headerIcon={isEdit ? <EditIcon /> : <PlusCircleIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isCreateNewOtherServiceLoading || isCreateNewOtherServiceLoadingInvoicing
        }}
        {...rest}
      >
        <S.Title>
          {!isEdit
            ? 'Preencha todos os campos para adicionar um novo serviço.'
            : 'Preencha todos os campos antes de confirmar a edição do serviço.'}
        </S.Title>

        {isEdit && (
          <S.Warning>
            Atenção: Ao confirmar esta edição, todos os dados do serviço previamente registrados serão excluídos.
          </S.Warning>
        )}

        <FormProvider {...methods}>
          <S.Form
            style={{ marginTop: !isEdit ? 12 : 0, marginBottom: !isMobile ? 10 : 0 }}
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <RowBox columns={2}>
              {clientData ? (
                <Input name="clientName" label="Cliente" required disabled />
              ) : clientsList?.length ? (
                <FormControl fullWidth>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      lineHeight: '14px',
                      fontWeight: 500,
                      color: emotionTheme.colors.gray.dark,
                      marginBottom: '6px'
                    }}
                  >
                    Cliente *
                  </span>
                  <MuiAutocomplete<{ value: string; label: string }, false, false, true>
                    freeSolo
                    options={clientsList.map((c) => ({
                      value: c.cdClienteNl,
                      label: `${c.cdClienteNl} - ${c.nomeCliente}`
                    }))}
                    value={(() => {
                      const code = methods.watch('clientCode')
                      const client = code ? clientsList.find((c) => c.cdClienteNl === code) : null
                      if (client) {
                        return { value: client.cdClienteNl, label: `${client.cdClienteNl} - ${client.nomeCliente}` }
                      }
                      return code || null
                    })()}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                    isOptionEqualToValue={(option, value) =>
                      typeof option === 'string' || typeof value === 'string'
                        ? option === value
                        : option.value === value.value
                    }
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue === 'object') {
                        const client = clientsList.find((c) => c.cdClienteNl === newValue.value)
                        if (client) {
                          setSelectedClient(client)
                          methods.setValue('clientCode', client.cdClienteNl)
                          methods.setValue('clientName', client.nomeCliente)
                          methods.setValue('clientType', client.tipoCliente)
                        }
                      } else if (typeof newValue === 'string') {
                        setSelectedClient(null)
                        methods.setValue('clientCode', newValue)
                        methods.setValue('clientName', newValue)
                      } else {
                        setSelectedClient(null)
                        methods.setValue('clientCode', '')
                        methods.setValue('clientName', '')
                      }
                    }}
                    onInputChange={(_, inputValue, reason) => {
                      if (reason === 'input') {
                        setSelectedClient(null)
                        methods.setValue('clientCode', inputValue)
                        methods.setValue('clientName', inputValue)
                      }
                    }}
                    noOptionsText="Nenhum cliente encontrado"
                    disabled={!canEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Selecione ou digite o cliente"
                        error={!!methods.formState.errors.clientCode}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '48px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: `1px solid ${emotionTheme.colors.gray.dark} !important`
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: `${emotionTheme.colors.primary} !important`
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              border: `2px solid ${emotionTheme.colors.primary} !important`
                            },
                            '& .MuiAutocomplete-input': {
                              padding: '2px 8px !important'
                            }
                          }
                        }}
                      />
                    )}
                  />
                  {methods.formState.errors.clientCode && (
                    <span
                      style={{
                        fontSize: '12px',
                        color: emotionTheme.colors.error.color,
                        paddingTop: '4px'
                      }}
                    >
                      {String(methods.formState.errors.clientCode.message)}
                    </span>
                  )}
                </FormControl>
              ) : (
                <Input name="clientName" label="Cliente" required disabled={!canEdit || !!clientData} />
              )}

              <RowBox columns={2}>
                <Select
                  required
                  name="clientType"
                  label="Tipo Cliente"
                  placeholder=""
                  options={[
                    { value: 'Principal', label: 'Principal' },
                    { value: 'Terceiro', label: 'Terceiro' }
                  ]}
                  disabled={!canEdit || !!clientData}
                />
                <DatePicker
                  required
                  name="dateOfBilling"
                  label="Data"
                  placeholder="DD/MM/AAAA"
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={!canEdit}
                />
              </RowBox>
            </RowBox>

            <RowBox columns={2}>
              <Input name="serviceDescription" label="Descrição Serviço" placeholder="-" required disabled={!canEdit} />

              <RowBox columns={2}>
                <Input
                  required
                  name="grossUnitValue"
                  label="Valor Bruto Unitário"
                  maskType="currency"
                  placeholder="R$ 0,00"
                  onBlur={({ target }) => {
                    methods.setValue('grossUnitValue', target.value)
                    handleTotalGrossValue()
                    handleTotalNetValue()
                  }}
                  disabled={!canEdit}
                />
                <Input
                  name="totalQuantity"
                  label="Qtd Total"
                  placeholder="0"
                  required
                  maskType="onlyNumbers"
                  onBlur={() => {
                    handleTotalGrossValue()
                    handleTotalNetValue()
                  }}
                  disabled={!canEdit}
                />
              </RowBox>
            </RowBox>

            <RowBox columns={3}>
              <Input
                required
                name="grossTotalValue"
                label="Valor Bruto Total"
                maskType="currency"
                placeholder="R$ 0,00"
                onBlur={({ target }) => methods.setValue('grossTotalValue', target.value)}
                disabled
              />
              <Input
                name="tax"
                label="Imposto (%)"
                placeholder="0%"
                maskType="percentage"
                onBlur={({ target }) => {
                  methods.setValue('tax', Number(target.value.replace(',', '.')).toFixed(2))
                  handleTotalNetValue()
                }}
                disabled={!canEdit}
              />
              <Input
                required
                name="netTotalValue"
                label="Valor Líquido Total"
                maskType="currency"
                placeholder="R$ 0,00"
                onBlur={({ target }) => methods.setValue('netTotalValue', target.value)}
                disabled
              />
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações"
                disabled={!canEdit}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        headerTitle="Alterações não salvas"
        message="Ao confirmar, os dados inseridos não serão salvos."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
